// rollback-coordinator.ts
//
// P7 §14/F14 test 46-49: abort SAU merge/deploy không được dừng đột ngột —
// phải đi qua chuỗi trạng thái an toàn:
//
//   STOPPING → reconcile (quan sát thực tại) → có approved rollback
//   procedure ⇒ ROLLING_BACK; KHÔNG có ⇒ WAITING_HUMAN kèm trạng thái môi
//   trường THẬT (không suy diễn "chưa deploy gì" một cách lạc quan).
//
//   ROLLING_BACK fail ⇒ WAITING_HUMAN, KHÔNG BAO GIỜ tự tuyên bố
//   "base untouched" (test 48) — chỉ người xác nhận trạng thái thật mới
//   đóng vòng này.
//
//   ABORTED chỉ đạt được SAU KHI hệ quả đã xử lý xong VÀ receipt đã ghi
//   (test 49) — không phải ngay khi lệnh abort được nhận.
//
// Module này CHỈ tính TOÁN quyết định + ghi operation_intent/result vào
// mission-store — việc áp dụng vào mission phase/executionStatus THẬT là
// của mission-state-machine.ts (Agent F, không sửa ở đây theo ownership).
import { randomUUID } from "node:crypto";
import type { MissionStore } from "./mission-store.js";

export interface RollbackProcedure {
  /** Recipe argv đã DUYỆT qua verification-profile — không tự bịa lệnh. */
  approved: boolean;
  authorityRef?: string;
}

export interface EnterStoppingInput {
  store: MissionStore;
  missionId: string;
  reason: string;
}

/** Test 46/47: abort-after-merge always enters STOPPING first — never a direct jump. */
export async function enterStoppingForAbortAfterMerge(
  input: EnterStoppingInput,
): Promise<{ operationId: string }> {
  const operationId = randomUUID();
  await input.store.commit([
    {
      event_id: `${operationId}-intent`,
      operation_id: operationId,
      delta: {
        kind: "operation_intent",
        operation_id: operationId,
        operation_kind: "abort_stopping",
        params: { mission_id: input.missionId, reason: input.reason },
      },
    },
  ]);
  return { operationId };
}

export interface EnvironmentState {
  /** True nếu quan sát thực tại xác nhận deploy/candidate đã ra môi trường. */
  candidateIsLive: boolean;
  observedAt: string;
  /** Mô tả bằng chứng — evidence phải ghi trạng thái THẬT, không suy diễn. */
  evidence: string;
}

export interface ReconcileAfterStoppingInput {
  store: MissionStore;
  stoppingOperationId: string;
  procedure: RollbackProcedure | undefined;
  environment: EnvironmentState;
}

export type ReconcileDecision =
  | { next: "rolling_back"; authorityRef: string }
  | { next: "waiting_human"; reason: string; environment: EnvironmentState };

/**
 * Test 46: có approved rollback procedure (authority) ⇒ ROLLING_BACK.
 * Test 47: KHÔNG có ⇒ WAITING_HUMAN, kèm environment state THẬT (không
 * lạc quan giả định "chưa có gì để rollback").
 */
export async function reconcileAfterStopping(
  input: ReconcileAfterStoppingInput,
): Promise<ReconcileDecision> {
  await input.store.commit([
    {
      event_id: `${input.stoppingOperationId}-result`,
      operation_id: input.stoppingOperationId,
      delta: {
        kind: "operation_result",
        operation_id: input.stoppingOperationId,
        outcome: "stopped",
      },
    },
  ]);

  if (input.procedure?.approved === true && input.procedure.authorityRef) {
    return { next: "rolling_back", authorityRef: input.procedure.authorityRef };
  }

  return {
    next: "waiting_human",
    reason:
      "Không có rollback procedure đã duyệt cho mission này ⇒ cần người quyết định, " +
      `môi trường hiện tại: ${input.environment.evidence} (quan sát lúc ${input.environment.observedAt}).`,
    environment: input.environment,
  };
}

// Rollback execution + ABORTED finalization (test 48-49) split into
// rollback-execution.ts to keep this file ≤200 lines — re-exported so
// callers only import ONE module for "rollback coordinator".
export {
  type FinalizeAbortedInput,
  finalizeAborted,
  type RollbackOutcome,
  type RunRollbackInput,
  runApprovedRollback,
} from "./rollback-execution.js";
